// @flow
import React, { useEffect, useRef } from 'react';
import { createTextLayer } from './PDFjs';

// iOS draws nothing on a canvas over 16.7M pixels (4096²), and caps all canvases together near 384 MB: a page is
// drawn with fewer pixels than the screen's when it would pass this, and only the pages near the screen keep one
export const MAX_CANVAS_PIXELS = 12000000;

// A zoom draws the page again once it stops; meanwhile the drawn canvas stretches to the new size
const REDRAW_DELAY = 160;

export const canvasRatio = (width: number, height: number, devicePixelRatio: number = window.devicePixelRatio || 1): number => (
  Math.min(devicePixelRatio, Math.sqrt(MAX_CANVAS_PIXELS / Math.max(1, width * height)))
);

// A canvas's memory goes when it has no pixels, not when it leaves the page (WebKit keeps it until collected)
const release = (canvas: any) => {
  canvas.width = 0; // eslint-disable-line no-param-reassign
  canvas.height = 0; // eslint-disable-line no-param-reassign
  canvas.remove();
};

const releaseAll = (holder: HTMLElement) => {
  Array.from(holder.querySelectorAll('canvas')).forEach(release);
};

type Props = {
  pdfDocument: Object,
  number: number,
  width: number,
  height: number,
  scale: number,
  // Near the screen: drawn. Away from it: an empty page of the same size.
  active: boolean,
  label: string,
};

const PDFPage = ({ pdfDocument, number, width, height, scale, active, label }: Props) => {
  const holderRef = useRef(null);
  const textRef = useRef(null);
  const drawnScale = useRef(0);

  useEffect(() => {
    const holder = holderRef.current;
    if (!holder) return undefined;
    if (!active) {
      releaseAll(holder);
      drawnScale.current = 0;
      return undefined;
    }
    if (drawnScale.current === scale) return undefined;
    let cancelled = false;
    let task = null;
    const timer = setTimeout(() => {
      pdfDocument.getPage(number).then((page) => {
        if (cancelled) return null;
        const viewport = page.getViewport({ scale });
        const ratio = canvasRatio(viewport.width, viewport.height);
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(viewport.width * ratio);
        canvas.height = Math.floor(viewport.height * ratio);
        task = page.render({
          canvasContext: canvas.getContext('2d'),
          viewport,
          transform: ratio === 1 ? undefined : [ratio, 0, 0, ratio, 0, 0],
        });
        return task.promise.then(() => {
          if (cancelled) {
            release(canvas);
            return;
          }
          // The new drawing replaces the old one only once it's done: a zoom never shows a blank page. Only a page's
          // first drawing fades in; a redraw over the stretched one would flash.
          if (!drawnScale.current) canvas.className = 'firstDrawing';
          releaseAll(holder);
          holder.appendChild(canvas);
          drawnScale.current = scale;
        }, () => release(canvas));
      }).catch(() => {});
    }, drawnScale.current ? REDRAW_DELAY : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (task) task.cancel();
    };
  }, [active, number, pdfDocument, scale]);

  useEffect(() => {
    const container = textRef.current;
    if (!container || !active) return undefined;
    let layer = null;
    let cancelled = false;
    pdfDocument.getPage(number).then((page) => {
      if (cancelled) return null;
      layer = createTextLayer(page, container);
      return layer.render();
    }).catch(() => {});
    return () => {
      cancelled = true;
      if (layer) layer.cancel();
      container.textContent = '';
    };
  }, [active, number, pdfDocument]);

  return (
    <div
      className="pdfPage"
      style={{ width, height, '--scale-factor': scale }}
      data-page-number={number}
      role="group"
      aria-label={label}
    >
      <div className="pdfPageCanvas" ref={holderRef} />
      <div className="textLayer" ref={textRef} />
    </div>
  );
};

export default React.memo<Props>(PDFPage);
